import React, { useRef, useImperativeHandle, forwardRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Table as TableIcon, Link2, Eraser } from "lucide-react";

const ALLOWED = {
  ALLOWED_TAGS: ["p", "br", "hr", "b", "strong", "i", "em", "u", "s", "sub", "sup", "ul", "ol", "li",
    "blockquote", "span", "div", "a", "img", "table", "thead", "tbody", "tfoot", "tr", "td", "th",
    "caption", "colgroup", "col", "h1", "h2", "h3", "h4", "h5", "h6", "font"],
  ALLOWED_ATTR: ["style", "class", "align", "dir", "lang", "href", "title", "target", "rel", "src", "alt",
    "width", "height", "colspan", "rowspan", "valign", "border", "cellpadding", "cellspacing", "color", "face", "size", "span"],
};

const clean = (html) => DOMPurify.sanitize(html || "", ALLOWED);

const Btn = ({ onClick, title, children, testid }) => (
  <button type="button" data-testid={testid} title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className="flex h-8 w-8 items-center justify-center rounded text-gmoaa-text-dark hover:bg-gmoaa-green-light">
    {children}
  </button>
);

export const RichTextEditor = forwardRef(({ initialHTML = "", onChange }, ref) => {
  const el = useRef(null);

  useEffect(() => {
    if (el.current && !el.current.innerHTML) el.current.innerHTML = clean(initialHTML);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    setHTML: (html) => { if (el.current) { el.current.innerHTML = clean(html); onChange && onChange(el.current.innerHTML); } },
    getHTML: () => (el.current ? el.current.innerHTML : ""),
  }));

  const emit = () => onChange && onChange(el.current.innerHTML);
  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); el.current.focus(); emit(); };

  const onPaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    const safe = html ? clean(html) : clean(`<p>${(text || "").replace(/\n/g, "<br>")}</p>`);
    document.execCommand("insertHTML", false, safe);
    emit();
  };

  const insertTable = () => {
    const r = parseInt(window.prompt("Rows?", "3") || "0", 10);
    const c = parseInt(window.prompt("Columns?", "3") || "0", 10);
    if (!r || !c) return;
    let t = "<table style='border-collapse:collapse;width:100%'>";
    for (let i = 0; i < r; i++) { t += "<tr>"; for (let j = 0; j < c; j++) t += "<td style='border:1px solid #334155;padding:4px 7px'>&nbsp;</td>"; t += "</tr>"; }
    t += "</table><p><br></p>";
    document.execCommand("insertHTML", false, t);
    emit();
  };

  const addLink = () => { const url = window.prompt("Link URL:", "https://"); if (url) exec("createLink", url); };

  return (
    <div className="rounded-xl border border-gmoaa-border-subtle bg-white">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-gmoaa-border-subtle p-1.5">
        <Btn testid="rte-bold" title="Bold" onClick={() => exec("bold")}><Bold size={15} /></Btn>
        <Btn testid="rte-italic" title="Italic" onClick={() => exec("italic")}><Italic size={15} /></Btn>
        <Btn testid="rte-underline" title="Underline" onClick={() => exec("underline")}><Underline size={15} /></Btn>
        <span className="mx-1 h-5 w-px bg-gmoaa-border-subtle" />
        <Btn testid="rte-ul" title="Bullet list" onClick={() => exec("insertUnorderedList")}><List size={15} /></Btn>
        <Btn testid="rte-ol" title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered size={15} /></Btn>
        <span className="mx-1 h-5 w-px bg-gmoaa-border-subtle" />
        <Btn testid="rte-left" title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft size={15} /></Btn>
        <Btn testid="rte-center" title="Align center" onClick={() => exec("justifyCenter")}><AlignCenter size={15} /></Btn>
        <Btn testid="rte-right" title="Align right" onClick={() => exec("justifyRight")}><AlignRight size={15} /></Btn>
        <span className="mx-1 h-5 w-px bg-gmoaa-border-subtle" />
        <Btn testid="rte-table" title="Insert table" onClick={insertTable}><TableIcon size={15} /></Btn>
        <Btn testid="rte-link" title="Insert link" onClick={addLink}><Link2 size={15} /></Btn>
        <Btn testid="rte-clear" title="Clear formatting" onClick={() => exec("removeFormat")}><Eraser size={15} /></Btn>
      </div>
      <div
        ref={el}
        data-testid="rte-editor"
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onPaste={onPaste}
        className="min-h-[320px] max-h-[520px] overflow-auto px-4 py-3 text-sm leading-relaxed outline-none [&_table]:my-2 [&_td]:border [&_td]:border-slate-400 [&_td]:p-1.5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6"
      />
    </div>
  );
});

RichTextEditor.displayName = "RichTextEditor";
